import React, { useState, useEffect, useRef } from 'react';
import {
  XMarkIcon,
  UserGroupIcon,
  ChartBarIcon,
  CogIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { employeeService } from '../services/employeeService';
import { departmentService } from '../services/departmentService';
import { companyService } from '../services/companyService';
import LeadAssignmentService from '../services/leadAssignmentService';
import LeadAssignmentNotificationService from '../services/leadAssignmentNotificationService';
import { authApi } from '../config/api';
import { jwtDecode } from 'jwt-decode';

const ChevronDownIcon = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    strokeWidth={2.5}
    stroke="currentColor"
    className={className}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
  </svg>
);

const CustomSelect = ({ label, value, onChange, options }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const selectedOption = options.find(opt => opt.value === value) || options[0];

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <label className="block text-[11px] md:text-sm font-medium text-gray-700 mb-1">
        {label}
      </label>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-2.5 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-[11px] md:text-sm bg-white text-left text-gray-900 shadow-sm hover:border-gray-400 transition-colors"
      >
        <span className="truncate pr-2">{selectedOption?.label}</span>
        <ChevronDownIcon className={`h-3.5 w-3.5 md:h-4 md:w-4 text-gray-500 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute z-[10010] mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto left-0 right-0">
          <ul className="py-0.5 text-[11px] md:text-sm text-gray-700">
            {options.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setIsOpen(false);
                  }}
                  className={`w-full text-left px-2.5 py-1.5 md:px-3 md:py-2 hover:bg-gray-50 truncate transition-colors ${
                    value === opt.value ? 'bg-blue-50 text-blue-600 font-semibold' : ''
                  }`}
                >
                  {opt.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

const BulkLeadAssignmentModal = ({
  isOpen,
  onClose,
  selectedLeads = [],
  onAssignmentComplete
}) => {
  // State management
  const [employees, setEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [distributionMethod, setDistributionMethod] = useState('equal');
  const [priority, setPriority] = useState('medium');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [distributionPreview, setDistributionPreview] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all'); // 'all', 'available', 'active'
  const [filterDepartment, setFilterDepartment] = useState('all'); // 'all' or department name
  const [sortBy, setSortBy] = useState('name'); // 'name', 'workload', 'performance'
  const [departments, setDepartments] = useState([]); // Available departments
  const [leadsToAssign, setLeadsToAssign] = useState([]); // Leads that will be assigned
  const [showLeadDetails, setShowLeadDetails] = useState(true); // Toggle lead details view
  const [assignmentMode, setAssignmentMode] = useState('replace'); // 'replace' or 'add' - NEW!
  const [activeMobileTab, setActiveMobileTab] = useState('employees'); // 'leads', 'employees', 'settings'

  // Load employees on modal open
  useEffect(() => {
    if (isOpen) {
      loadEmployees();
      resetForm();
      // Initialize leadsToAssign - exclude already assigned leads by default
      setLeadsToAssign(selectedLeads.map(lead => {
        const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                          lead.assignedEmployeeName;
        return { 
          ...lead, 
          include: !isAssigned // Unassigned leads are included by default
        };
      }));
    }
  }, [isOpen, selectedLeads]);

  // Generate preview when employees or method changes
  useEffect(() => {
    const includedLeads = leadsToAssign.filter(l => l.include);
    if (selectedEmployees.length > 0 && includedLeads.length > 0) {
      generateDistributionPreview();
    }
  }, [selectedEmployees, distributionMethod, leadsToAssign]);

  const resetForm = () => {
    setSelectedEmployees([]);
    setDistributionMethod('equal');
    setPriority('medium');
    setNotes('');
    setShowPreview(false);
    setDistributionPreview([]);
    setError('');
    setActiveMobileTab('employees');
    // Reset leads - exclude already assigned leads by default
    setLeadsToAssign(selectedLeads.map(lead => {
      const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                        lead.assignedEmployeeName;
      return { 
        ...lead, 
        include: !isAssigned // Unassigned leads are included by default
      };
    }));
  };

  const toggleLeadInclusion = (leadId) => {
    setLeadsToAssign(prev => prev.map(lead => 
      lead._id === leadId ? { ...lead, include: !lead.include } : lead
    ));
  };

  const toggleAllLeads = () => {
    const allIncluded = leadsToAssign.every(l => l.include);
    setLeadsToAssign(prev => prev.map(lead => ({ ...lead, include: !allIncluded })));
  };

  const selectOnlyUnassigned = () => {
    setLeadsToAssign(prev => prev.map(lead => {
      const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                        lead.assignedEmployeeName;
      return { ...lead, include: !isAssigned };
    }));
  };

  const selectOnlyAssigned = () => {
    setLeadsToAssign(prev => prev.map(lead => {
      const isAssigned = (lead.assignedEmployees && lead.assignedEmployees.length > 0) || 
                        lead.assignedEmployeeName;
      return { ...lead, include: isAssigned };
    }));
  };

  const getAssignedEmployeeName = (lead) => {
    if (lead.assignedEmployees && lead.assignedEmployees.length > 0) {
      return lead.assignedEmployees.map(emp => emp.name).join(', ');
    }
    if (lead.assignedEmployeeName) {
      return lead.assignedEmployeeName;
    }
    return 'Unassigned';
  };

  const loadEmployees = async () => {
    setLoadingData(true);
    setError('');
    const debugLogs = [];

    try {
      // 1. Fetch employees first
      const result = await employeeService.getAllEmployees();
      const employees = result.success ? (result.data || []) : [];
      debugLogs.push(`Employees loaded: ${employees.length}`);

      // 2. Extract unique department IDs
      const uniqueDeptIds = new Set();
      employees.forEach(emp => {
        const rawDept = emp.department;
        if (Array.isArray(rawDept)) {
          rawDept.forEach(d => {
            const id = d?._id || d;
            if (id && typeof id === 'string' && id.match(/^[0-9a-fA-F]{24}$/)) {
              uniqueDeptIds.add(id);
            }
          });
        } else if (rawDept) {
          const id = rawDept._id || rawDept;
          if (id && typeof id === 'string' && id.match(/^[0-9a-fA-F]{24}$/)) {
            uniqueDeptIds.add(id);
          }
        }
      });
      debugLogs.push(`Unique IDs: ${uniqueDeptIds.size}`);

      // 3. Resolve departments
      let deptMap = {};

      // Get company ID to query batch departments
      let companyId = null;
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          const decoded = jwtDecode(token);
          if (decoded) {
            companyId = decoded.role === 'company' ? decoded.id : decoded.companyId;
          }
        } catch (e) {}
      }
      if (!companyId) {
        companyId = localStorage.getItem('companyId');
      }

      if (companyId) {
        try {
          const res = await authApi.get(`/departments/company/${companyId}`);
          const data = res.data?.data || res.data?.departments || (Array.isArray(res.data) ? res.data : []);
          debugLogs.push(`Company Depts: ${data.length}`);
          data.forEach(d => {
            const id = d?._id || d?.id;
            if (id) {
              deptMap[id] = d.name || d.departmentName || d.title;
            }
          });
        } catch (err) {
          debugLogs.push(`Company Depts error: ${err.message}`);
        }
      }

      // Query unresolved IDs individually
      let resolvedCount = 0;
      for (const deptId of uniqueDeptIds) {
        if (!deptMap[deptId]) {
          try {
            const res = await authApi.get(`/departments/${deptId}`);
            const d = res.data?.data || res.data;
            if (d && (d.name || d.departmentName || d.title)) {
              deptMap[deptId] = d.name || d.departmentName || d.title;
              resolvedCount++;
            }
          } catch (authErr) {
            try {
              const res = await departmentService.getDepartmentById(deptId);
              if (res.success && res.data) {
                const d = res.data;
                deptMap[deptId] = d.name || d.departmentName || d.title;
                resolvedCount++;
              }
            } catch (apiErr) {}
          }
        }
      }
      debugLogs.push(`Resolved Individually: ${resolvedCount}`);
      debugLogs.push(`DeptMap: ${JSON.stringify(deptMap)}`);

      console.log('🗺️ Department lookup status:', debugLogs.join(' | '));

      if (result.success) {
        // Enhance employee data with mock statistics for demonstration
        const enhancedEmployees = employees.map(emp => {
          let empDeptName = 'No Department';
          let rawDept = emp.department;
          if (Array.isArray(rawDept) && rawDept.length > 0) {
            const firstDept = rawDept[0];
            const deptId = firstDept?._id || firstDept;
            empDeptName = firstDept?.name || deptMap[deptId] || deptId || 'No Department';
          } else if (rawDept) {
            const deptId = rawDept._id || rawDept;
            empDeptName = rawDept.name || deptMap[deptId] || deptId || 'No Department';
          }

          return {
            ...emp,
            currentWorkload: Math.floor(Math.random() * 50) + 10, // Mock current leads count
            performanceScore: Math.floor(Math.random() * 30) + 70, // Mock performance 70-100
            isAvailable: Math.random() > 0.2, // 80% availability
            lastAssignedDate: new Date(Date.now() - Math.random() * 7 * 24 * 60 * 60 * 1000),
            departmentName: empDeptName,
            role: emp.role || 'Sales Executive'
          };
        });

        setEmployees(enhancedEmployees);

        // Extract unique departments using the resolved names
        const uniqueDepts = [...new Set(enhancedEmployees.map(emp => emp.departmentName))].sort();
        console.log('📊 Unique departments found:', uniqueDepts);
        setDepartments(uniqueDepts);
      } else {
        setError('Failed to load employees');
      }
    } catch (error) {
      console.error('Error loading employees:', error);
      setError('Failed to load employee data');
    }

    setLoadingData(false);
  };

  const generateDistributionPreview = () => {
    const includedLeads = leadsToAssign.filter(l => l.include);
    
    if (selectedEmployees.length === 0 || includedLeads.length === 0) {
      setDistributionPreview([]);
      return;
    }

    let distribution = [];
    const totalLeads = includedLeads.length;
    const totalEmployees = selectedEmployees.length;

    switch (distributionMethod) {
      case 'equal':
        // Equal distribution
        const leadsPerEmployee = Math.floor(totalLeads / totalEmployees);
        const remainder = totalLeads % totalEmployees;

        selectedEmployees.forEach((emp, index) => {
          const assignedCount = leadsPerEmployee + (index < remainder ? 1 : 0);
          distribution.push({
            employee: emp,
            assignedCount,
            percentage: ((assignedCount / totalLeads) * 100).toFixed(1)
          });
        });
        break;

      case 'weighted':
        // Weighted by performance and availability
        const weights = selectedEmployees.map(emp => {
          const performanceWeight = emp.performanceScore / 100;
          const availabilityWeight = emp.isAvailable ? 1 : 0.5;
          const workloadWeight = Math.max(0.1, (100 - emp.currentWorkload) / 100);
          return performanceWeight * availabilityWeight * workloadWeight;
        });

        const totalWeight = weights.reduce((sum, weight) => sum + weight, 0);

        selectedEmployees.forEach((emp, index) => {
          const assignedCount = Math.round((weights[index] / totalWeight) * totalLeads);
          distribution.push({
            employee: emp,
            assignedCount,
            percentage: ((assignedCount / totalLeads) * 100).toFixed(1)
          });
        });
        break;

      case 'balanced':
        // Balanced by current workload
        const maxWorkload = Math.max(...selectedEmployees.map(emp => emp.currentWorkload));
        const balanceWeights = selectedEmployees.map(emp => maxWorkload - emp.currentWorkload + 1);
        const totalBalanceWeight = balanceWeights.reduce((sum, weight) => sum + weight, 0);

        selectedEmployees.forEach((emp, index) => {
          const assignedCount = Math.round((balanceWeights[index] / totalBalanceWeight) * totalLeads);
          distribution.push({
            employee: emp,
            assignedCount,
            percentage: ((assignedCount / totalLeads) * 100).toFixed(1)
          });
        });
        break;

      default:
        distribution = [];
    }

    // Ensure total adds up to selectedLeads.length
    const totalAssigned = distribution.reduce((sum, item) => sum + item.assignedCount, 0);
    if (totalAssigned !== totalLeads && distribution.length > 0) {
      const diff = totalLeads - totalAssigned;
      distribution[0].assignedCount += diff;
      distribution[0].percentage = ((distribution[0].assignedCount / totalLeads) * 100).toFixed(1);
    }

    setDistributionPreview(distribution);
  };

  const handleEmployeeToggle = (employee) => {
    setSelectedEmployees(prev => {
      const isSelected = prev.some(emp => emp._id === employee._id);
      if (isSelected) {
        return prev.filter(emp => emp._id !== employee._id);
      } else {
        return [...prev, employee];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredEmployees = getFilteredEmployees();
    if (selectedEmployees.length === filteredEmployees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees);
    }
  };

  const getFilteredEmployees = () => {
    let filtered = employees;

    // Apply status filter
    switch (filterStatus) {
      case 'available':
        filtered = filtered.filter(emp => emp.isAvailable);
        break;
      case 'active':
        filtered = filtered.filter(emp => emp.currentWorkload < 40);
        break;
      default:
        // 'all' - no filtering
        break;
    }

    // Apply department filter
    if (filterDepartment !== 'all') {
      filtered = filtered.filter(emp => emp.departmentName === filterDepartment);
    }

    // Apply sorting
    switch (sortBy) {
      case 'workload':
        filtered = [...filtered].sort((a, b) => a.currentWorkload - b.currentWorkload);
        break;
      case 'performance':
        filtered = [...filtered].sort((a, b) => b.performanceScore - a.performanceScore);
        break;
      case 'name':
      default:
        filtered = [...filtered].sort((a, b) =>
          (a.teamMemberName || a.name || '').localeCompare(b.teamMemberName || b.name || '')
        );
        break;
    }

    return filtered;
  };

  const handleBulkAssignment = async () => {
    const includedLeads = leadsToAssign.filter(l => l.include);
    
    if (selectedEmployees.length === 0) {
      setError('Please select at least one employee');
      return;
    }

    if (includedLeads.length === 0) {
      setError('Please select at least one lead to assign');
      return;
    }

    if (distributionPreview.length === 0) {
      setError('No distribution preview available');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Create assignment batches based on distribution
      const assignments = [];
      let leadIndex = 0;

      for (const item of distributionPreview) {
        if (item.assignedCount > 0) {
          const leadsForEmployee = includedLeads.slice(leadIndex, leadIndex + item.assignedCount);

          for (const lead of leadsForEmployee) {
            assignments.push({
              leadId: lead._id,
              employeeId: item.employee._id,
              employeeName: item.employee.teamMemberName || item.employee.name,
              priority,
              notes: notes || `Bulk assignment - ${distributionMethod} distribution`,
              assignedDate: new Date().toISOString(),
              distributionMethod,
              assignmentMode: assignmentMode // 'replace' or 'add'
            });
          }

          leadIndex += item.assignedCount;
        }
      }

      // Execute bulk assignment with enhanced distribution
      const result = await LeadAssignmentService.bulkAssignLeadsWithDistribution(assignments);

      if (result.success) {
        // Group by employeeId to send notifications
        const assignmentsByEmployee = {};
        assignments.forEach(a => {
          if (!assignmentsByEmployee[a.employeeId]) {
            assignmentsByEmployee[a.employeeId] = [];
          }
          assignmentsByEmployee[a.employeeId].push(a);
        });

        // Send notifications for each employee
        for (const employeeId of Object.keys(assignmentsByEmployee)) {
          const empAssignments = assignmentsByEmployee[employeeId];
          const leadCount = empAssignments.length;
          const firstLead = includedLeads.find(l => l._id === empAssignments[0].leadId);
          const leadName = firstLead ? firstLead.name : 'a lead';

          // Global Notification via authApi for dropdown bell and popup display
          try {
            await authApi.post('/notifications/bulk', {
              recipients: [employeeId],
              type: 'lead',
              title: 'Lead Assigned',
              message: leadCount === 1
                ? `Lead "${leadName}" has been assigned to you.`
                : `${leadCount} leads have been assigned to you.`,
              meta: {
                leadId: leadCount === 1 ? empAssignments[0].leadId : null,
                companyName: firstLead ? firstLead.companyName || firstLead.company || '' : ''
              }
            });
          } catch (err) {
            console.error(`Failed to send global notification to ${employeeId}:`, err);
          }

          // Lead-specific notification via lead service
          try {
            await LeadAssignmentNotificationService.createAssignmentNotification({
              leadId: leadCount === 1 ? empAssignments[0].leadId : null,
              leadIds: leadCount > 1 ? empAssignments.map(a => a.leadId) : null,
              assignedTo: employeeId,
              notes: notes || `Bulk assignment - ${distributionMethod} distribution`,
              priority: priority,
              leadName: leadCount === 1 ? leadName : `${leadCount} Leads`,
              leadEmail: leadCount === 1 && firstLead ? firstLead.email || '' : '',
              leadPhone: leadCount === 1 && firstLead ? firstLead.phone || '' : ''
            });
          } catch (err) {
            console.error(`Failed to create assignment notification for ${employeeId}:`, err);
          }
        }

        window.dispatchEvent(new Event('lead-assigned'));

        onAssignmentComplete && onAssignmentComplete({
          success: true,
          message: `Successfully assigned ${includedLeads.length} leads to ${selectedEmployees.length} employees`,
          assignments: assignments
        });
        onClose();
      } else {
        setError(result.message || 'Failed to assign leads');
      }
    } catch (error) {
      console.error('Error in bulk assignment:', error);
      setError('Failed to assign leads. Please try again.');
    }

    setLoading(false);
  };

  if (!isOpen) return null;

  const filteredEmployees = getFilteredEmployees();
  const allSelected = selectedEmployees.length === filteredEmployees.length && filteredEmployees.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999]">
      <div className="bg-white rounded-xl shadow-2xl w-[94vw] sm:w-full max-w-6xl h-[85vh] lg:h-auto max-h-[90vh] overflow-hidden relative z-[10000] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-3.5 sm:p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
          <div className="flex items-center space-x-3">
            <UserGroupIcon className="h-6 w-6" />
            <div>
              <h2 className="text-xl font-bold">Bulk Lead Assignment</h2>
              <p className="text-blue-100 text-sm">
                Assign {selectedLeads.length} leads to multiple employees
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-col lg:flex-row flex-1 overflow-hidden relative">
          {/* Mobile Tabs Header */}
          <div className="lg:hidden flex border-b border-gray-200 bg-white sticky top-0 z-20 shrink-0">
            {showLeadDetails && (
              <button
                onClick={() => setActiveMobileTab('leads')}
                className={`flex-1 py-3 text-center text-sm font-semibold border-b-2 transition-colors ${
                  activeMobileTab === 'leads'
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                Leads ({leadsToAssign.filter(l => l.include).length})
              </button>
            )}
            <button
              onClick={() => setActiveMobileTab('employees')}
              className={`flex-1 py-3 text-center text-sm font-semibold border-b-2 transition-colors ${
                activeMobileTab === 'employees'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Employees ({selectedEmployees.length})
            </button>
            <button
              onClick={() => setActiveMobileTab('settings')}
              className={`flex-1 py-3 text-center text-sm font-semibold border-b-2 transition-colors ${
                activeMobileTab === 'settings'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Settings & Preview
            </button>
          </div>

          {/* Lead Details Panel - Collapsible */}
          {showLeadDetails && (
            <div className={`w-full lg:w-80 border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col bg-gray-50 flex-1 lg:h-full min-h-0 overflow-hidden flex-shrink-0 ${
              activeMobileTab === 'leads' ? 'flex' : 'hidden lg:flex'
            }`}>
              <div className="p-4 border-b border-gray-200 bg-white">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900">Selected Leads</h3>
                  <button
                    onClick={() => {
                      setShowLeadDetails(false);
                      if (activeMobileTab === 'leads') {
                        setActiveMobileTab('employees');
                      }
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                
                {/* Lead Count and Stats */}
                <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="font-semibold text-blue-900">
                        {leadsToAssign.filter(l => l.include).length}
                      </span>
                      <span className="text-blue-700"> of {leadsToAssign.length} leads selected</span>
                    </div>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs">
                    <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">
                      ✓ {leadsToAssign.filter(l => !l.include && ((l.assignedEmployees && l.assignedEmployees.length > 0) || l.assignedEmployeeName)).length} Assigned (Excluded)
                    </span>
                    <span className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                      ○ {leadsToAssign.filter(l => l.include && !((l.assignedEmployees && l.assignedEmployees.length > 0) || l.assignedEmployeeName)).length} Unassigned (Included)
                    </span>
                  </div>
                </div>

                {/* Quick Filter Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
                  <button
                    onClick={toggleAllLeads}
                    className="text-xs px-2 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 font-medium w-full text-center"
                  >
                    {leadsToAssign.every(l => l.include) ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={selectOnlyUnassigned}
                    className="text-xs px-2 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 font-medium w-full text-center"
                  >
                    Only Unassigned
                  </button>
                  <button
                    onClick={selectOnlyAssigned}
                    className="text-xs px-2 py-1.5 bg-yellow-600 text-white rounded hover:bg-yellow-700 font-medium w-full text-center"
                  >
                    Only Assigned
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                <div className="space-y-2">
                  {leadsToAssign.map((lead) => {
                    const assignedTo = getAssignedEmployeeName(lead);
                    const isAssigned = assignedTo !== 'Unassigned';
                    
                    return (
                      <div
                        key={lead._id}
                        onClick={() => toggleLeadInclusion(lead._id)}
                        className={`p-1.5 md:p-2.5 rounded-lg border cursor-pointer transition-all ${
                          lead.include
                            ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                            : 'border-gray-200 bg-gray-50 opacity-70'
                        }`}
                      >
                        <div className="flex items-center gap-2 md:gap-3">
                          {/* Checkbox */}
                          <div className={`w-3.5 h-3.5 md:w-4 md:h-4 rounded border flex items-center justify-center shrink-0 ${
                            lead.include ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'
                          }`}>
                            {lead.include && (
                              <CheckCircleIcon className="h-2 w-2 md:h-2.5 md:w-2.5 text-white" />
                            )}
                          </div>
                          
                          {/* Lead Information */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-1 md:gap-2">
                              <h4 className="font-semibold text-gray-900 text-xs md:text-sm truncate">
                                {lead.name}
                              </h4>
                              <div className="flex items-center gap-1 shrink-0">
                                {lead.budget && (
                                  <span className="bg-green-100 text-green-700 text-[9px] md:text-[10px] font-medium px-1 md:px-1.5 py-0.5 rounded">
                                    ₹{lead.budget}
                                  </span>
                                )}
                                {lead.status && (
                                  <span className="bg-gray-100 text-gray-600 text-[9px] md:text-[10px] px-1 md:px-1.5 py-0.5 rounded uppercase font-semibold">
                                    {lead.status}
                                  </span>
                                )}
                              </div>
                            </div>
                            
                            <div className="mt-0.5 md:mt-1 flex flex-wrap items-center gap-x-1.5 md:gap-x-2 gap-y-0.5 text-[10px] md:text-xs text-gray-500">
                              <span className="truncate max-w-[120px] md:max-w-[150px]" title={lead.email}>{lead.email || 'No email'}</span>
                              <span>•</span>
                              {isAssigned ? (
                                <span className="text-amber-700 flex items-center gap-1 bg-amber-50 px-1 md:px-1.5 py-0.5 rounded border border-amber-200 text-[9px] md:text-[10px]" title={`Assigned to: ${assignedTo}`}>
                                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                                  Assigned: <span className="truncate max-w-[60px] md:max-w-[80px] font-medium">{assignedTo}</span>
                                  {!lead.include && <span className="text-red-500 font-semibold ml-0.5 md:ml-1">(Excluded)</span>}
                                </span>
                              ) : (
                                <span className="text-green-700 flex items-center gap-1 bg-green-50 px-1 md:px-1.5 py-0.5 rounded border border-green-200 text-[9px] md:text-[10px]">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                                  Unassigned
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Toggle Button when Lead Details is Hidden */}
          {!showLeadDetails && (
            <button
              onClick={() => setShowLeadDetails(true)}
              className="hidden lg:block absolute left-0 top-1/2 -translate-y-1/2 bg-blue-600 text-white p-2 rounded-r-lg shadow-lg hover:bg-blue-700 z-10"
              title="Show lead details"
            >
              <UserGroupIcon className="h-5 w-5" />
            </button>
          )}

          {/* Left Panel - Employee Selection */}
          <div className={`w-full lg:w-1/2 border-b lg:border-b-0 lg:border-r border-gray-200 flex flex-col flex-1 lg:h-full min-h-0 overflow-hidden flex-shrink-0 lg:flex-shrink ${
            activeMobileTab === 'employees' ? 'flex' : 'hidden lg:flex'
          }`}>
            <div className="p-3 md:p-4 border-b border-gray-200 bg-gray-50">
              <h3 className="text-md md:text-lg font-semibold text-gray-900 mb-2 md:mb-4">Select Employees</h3>

              {/* Filters and Controls */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-3 mb-3 md:mb-4">
                <CustomSelect
                  label="Filter Status"
                  value={filterStatus}
                  onChange={setFilterStatus}
                  options={[
                    { value: 'all', label: 'All Employees' },
                    { value: 'available', label: 'Available Only' },
                    { value: 'active', label: 'Low Workload' }
                  ]}
                />
                <CustomSelect
                  label="Department"
                  value={filterDepartment}
                  onChange={setFilterDepartment}
                  options={[
                    { value: 'all', label: 'All Departments' },
                    ...departments.map(dept => ({ value: dept, label: dept }))
                  ]}
                />
                <CustomSelect
                  label="Sort By"
                  value={sortBy}
                  onChange={setSortBy}
                  options={[
                    { value: 'name', label: 'Name' },
                    { value: 'workload', label: 'Current Workload' },
                    { value: 'performance', label: 'Performance Score' }
                  ]}
                />
              </div>

              {/* Select All Button */}
              <button
                onClick={handleSelectAll}
                className="w-full px-3 py-1.5 md:px-4 md:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs md:text-sm font-medium"
              >
                {allSelected ? 'Deselect All' : 'Select All'} ({filteredEmployees.length})
              </button>

              <div className="mt-1.5 md:mt-2 text-xs md:text-sm text-gray-600">
                Selected: {selectedEmployees.length} / {filteredEmployees.length} employees
              </div>
            </div>

            {/* Employee List */}
            <div className="flex-1 overflow-y-auto p-3 md:p-4">
              {loadingData ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredEmployees.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <UserGroupIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No employees found</p>
                </div>
              ) : (
                <div className="space-y-2 md:space-y-3">
                  {filteredEmployees.map((employee) => {
                    const isSelected = selectedEmployees.some(emp => emp._id === employee._id);
                    return (
                      <div
                        key={employee._id}
                        onClick={() => handleEmployeeToggle(employee)}
                        className={`p-2 md:p-3 border rounded-lg cursor-pointer transition-all ${isSelected
                          ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                          : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                          }`}
                      >
                        <div className="flex items-center space-x-2 md:space-x-3">
                          {/* Checkbox */}
                          <div className={`w-3.5 h-3.5 md:w-4 md:h-4 rounded border flex items-center justify-center shrink-0 ${isSelected ? 'bg-blue-600 border-blue-600' : 'border-gray-300'
                            }`}>
                            {isSelected && (
                              <CheckCircleIcon className="h-2 w-2 md:h-2.5 md:w-2.5 text-white" />
                            )}
                          </div>

                          {/* Status Dot */}
                          <div className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full shrink-0 ${employee.isAvailable ? 'bg-green-500' : 'bg-red-500'}`} 
                               title={employee.isAvailable ? 'Available' : 'Busy'} />

                          {/* Info Block */}
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                              <h4 className="font-semibold text-gray-900 text-xs md:text-sm truncate">
                                {employee.teamMemberName || employee.name}
                              </h4>
                              <span className="text-[10px] md:text-xs text-gray-500 truncate sm:ml-2">
                                {employee.role} • {employee.departmentName}
                              </span>
                            </div>
                            
                            {/* Compact Stats */}
                            <div className="mt-0.5 md:mt-1 flex items-center space-x-3 md:space-x-4 text-[10px] md:text-xs text-gray-500">
                              <span>
                                Workload: <strong className="text-gray-700">{employee.currentWorkload} leads</strong>
                              </span>
                              <span>•</span>
                              <span>
                                Performance: <strong className="text-gray-700">{employee.performanceScore}%</strong>
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Distribution Settings & Preview */}
          <div className={`w-full lg:w-1/2 flex flex-col flex-1 lg:h-full min-h-0 overflow-y-auto lg:overflow-hidden flex-shrink-0 lg:flex-shrink ${
            activeMobileTab === 'settings' ? 'flex' : 'hidden lg:flex'
          }`}>
            <div className="p-3 md:p-4 border-b border-gray-200 bg-gray-50 lg:overflow-y-auto lg:max-h-[55%] shrink-0">
              <h3 className="text-md md:text-lg font-semibold text-gray-900 mb-2 md:mb-4">Distribution Settings</h3>

              {/* Distribution Method */}
              <div className="mb-3 md:mb-4">
                <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                  Distribution Method
                </label>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex items-center p-2 md:p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="distributionMethod"
                      value="equal"
                      checked={distributionMethod === 'equal'}
                      onChange={(e) => setDistributionMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-xs md:text-sm font-medium text-gray-900">Equal Distribution</div>
                      <div className="text-[11px] md:text-xs text-gray-500">Distribute leads equally among selected employees</div>
                    </div>
                  </label>

                  <label className="flex items-center p-2 md:p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="distributionMethod"
                      value="weighted"
                      checked={distributionMethod === 'weighted'}
                      onChange={(e) => setDistributionMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-xs md:text-sm font-medium text-gray-900">Performance Weighted</div>
                      <div className="text-[11px] md:text-xs text-gray-500">Distribute based on performance and availability</div>
                    </div>
                  </label>

                  <label className="flex items-center p-2 md:p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
                    <input
                      type="radio"
                      name="distributionMethod"
                      value="balanced"
                      checked={distributionMethod === 'balanced'}
                      onChange={(e) => setDistributionMethod(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="text-xs md:text-sm font-medium text-gray-900">Workload Balanced</div>
                      <div className="text-[11px] md:text-xs text-gray-500">Balance based on current workload</div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Assignment Mode - NEW! */}
              <div className="mb-3 md:mb-4 p-3 md:p-4 bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg">
                <label className="block text-xs md:text-sm font-semibold text-gray-900 mb-2 md:mb-3">
                  📋 Assignment Mode
                </label>
                <div className="grid grid-cols-1 gap-2">
                  <label className="flex items-start p-2.5 md:p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-all bg-white">
                    <input
                      type="radio"
                      name="assignmentMode"
                      value="replace"
                      checked={assignmentMode === 'replace'}
                      onChange={(e) => setAssignmentMode(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-xs md:text-sm font-semibold text-gray-900 flex items-center gap-1.5 md:gap-2">
                        🔄 Replace Assignment
                        <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Default</span>
                      </div>
                      <div className="text-[11px] md:text-xs text-gray-500 mt-0.5 md:mt-1">
                        Remove old employees and assign to new ones
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-400 mt-0.5 md:mt-1 italic">
                        Example: Lead assigned to A → Assign to B → Lead now only with B
                      </div>
                    </div>
                  </label>

                  <label className="flex items-start p-2.5 md:p-3 border-2 rounded-lg cursor-pointer hover:bg-white transition-all bg-white">
                    <input
                      type="radio"
                      name="assignmentMode"
                      value="add"
                      checked={assignmentMode === 'add'}
                      onChange={(e) => setAssignmentMode(e.target.value)}
                      className="mt-1 mr-3"
                    />
                    <div className="flex-1">
                      <div className="text-xs md:text-sm font-semibold text-gray-900 flex items-center gap-1.5 md:gap-2">
                        ➕ Add to Assignment
                        <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Team Mode</span>
                      </div>
                      <div className="text-[11px] md:text-xs text-gray-500 mt-0.5 md:mt-1">
                        Keep old employees and add new ones (Team collaboration)
                      </div>
                      <div className="text-[10px] md:text-xs text-gray-400 mt-0.5 md:mt-1 italic">
                        Example: Lead assigned to A → Assign to B → Lead now with A + B
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {/* Priority and Notes */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <CustomSelect
                  label="Priority"
                  value={priority}
                  onChange={setPriority}
                  options={[
                    { value: 'low', label: 'Low' },
                    { value: 'medium', label: 'Medium' },
                    { value: 'high', label: 'High' },
                    { value: 'urgent', label: 'Urgent' }
                  ]}
                />
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <input
                    type="text"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Assignment notes..."
                    className="w-full px-2 py-1.5 md:px-3 md:py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs md:text-sm"
                  />
                </div>
              </div>
            </div>

            {/* Distribution Preview */}
            <div className="p-3 md:p-4 lg:flex-1 lg:overflow-y-auto bg-white">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-md font-semibold text-gray-900">Distribution Preview</h4>
                <ChartBarIcon className="h-5 w-5 text-gray-500" />
              </div>

              {selectedEmployees.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <InformationCircleIcon className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>Select employees to see distribution preview</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {distributionPreview.map((item, index) => (
                    <div key={item.employee._id} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-medium text-gray-900">
                          {item.employee.teamMemberName || item.employee.name}
                        </div>
                        <div className="text-lg font-bold text-blue-600">
                          {item.assignedCount} leads
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                        <span>{item.employee.role}</span>
                        <span>{item.percentage}% of total</span>
                      </div>

                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${item.percentage}%` }}
                        ></div>
                      </div>

                      <div className="mt-2 text-xs text-gray-500">
                        Current workload: {item.employee.currentWorkload} | Performance: {item.employee.performanceScore}%
                      </div>
                    </div>
                  ))}

                  {/* Summary */}
                  <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center space-x-2 mb-2">
                      <InformationCircleIcon className="h-5 w-5 text-blue-600" />
                      <span className="font-medium text-blue-900">Assignment Summary</span>
                    </div>
                    <div className="text-sm text-blue-800">
                      <div>Total Leads: {leadsToAssign.filter(l => l.include).length} (of {leadsToAssign.length} selected)</div>
                      <div>Selected Employees: {selectedEmployees.length}</div>
                      <div>Distribution Method: {distributionMethod.charAt(0).toUpperCase() + distributionMethod.slice(1)}</div>
                      <div>Priority Level: {priority.charAt(0).toUpperCase() + priority.slice(1)}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 p-3 md:p-5 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4 w-full sm:w-auto justify-start">
            {error && (
              <div className="flex items-center space-x-2 text-red-600">
                <ExclamationTriangleIcon className="h-5 w-5" />
                <span className="text-sm">{error}</span>
              </div>
            )}
          </div>

          <div className="flex items-center justify-between sm:justify-end space-x-3 w-full sm:w-auto flex-1 sm:flex-initial">
            <button
              onClick={onClose}
              className="flex-1 sm:flex-none text-center justify-center px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkAssignment}
              disabled={loading || selectedEmployees.length === 0 || loadingData}
              className="flex-1 sm:flex-none text-center justify-center px-3 py-1.5 md:px-5 md:py-2 text-xs md:text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
            >
              {loading && (
                <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white mr-1.5"></div>
              )}
              <span className="truncate">
                {loading ? 'Assigning...' : `Assign to ${selectedEmployees.length} Employee${selectedEmployees.length !== 1 ? 's' : ''}`}
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkLeadAssignmentModal;
